import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getSmsGateway, updateSmsGateway } from '../../../../Redux/Actions/action';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import ButtonContained from '../../../Common/ButtonContained';
import CommonTextField from '../../../Common/CommonTextField';
import TextLabel from '../../../Common/CommonTextLabel';
import ErrorHandler from '../../../../Utils/handler';

const SmsGateway = () => {
    const dispatch = useDispatch();
    
    const [formData, setFormData] = useState({
        apiKey: '',
        senderId: '',
        templateId: '',
        baseUrl: '',
        isActive: true
    });
    const [error, setError] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setError(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        let newErrors = {};
        if (!formData.apiKey?.trim()) newErrors.apiKey = 'API Key is required';
        if (!formData.senderId?.trim()) newErrors.senderId = 'Sender ID is required';
        if (!formData.baseUrl?.trim()) newErrors.baseUrl = 'Base URL is required';
        setError(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const _getSmsGateway = async () => {
        setIsLoading(true);
        dispatch(showLoader(true));
        try {
            const res = await dispatch(getSmsGateway()).unwrap();
            if (res?.code === 1 && res?.gatewayRecord) {
                setFormData({
                    apiKey: res.gatewayRecord.apiKey || '',
                    senderId: res.gatewayRecord.senderId || '',
                    templateId: res.gatewayRecord.templateId || '',
                    baseUrl: res.gatewayRecord.baseUrl || '',
                    isActive: res.gatewayRecord.isActive ?? true
                });
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            setIsLoading(false);
            dispatch(showLoader(false));
        }
    };

    const _updateSmsGateway = async () => {
        if (!validate()) return;
        
        dispatch(showLoader(true));
        try {
            const res = await dispatch(updateSmsGateway(formData)).unwrap();
            if (res?.code === 1) {
                toast.success('SMS Gateway settings updated successfully');
                _getSmsGateway();
            } else {
                toast.error(res?.message || 'Failed to update SMS Gateway settings');
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        _getSmsGateway();
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>
                        SMS Gateway Configuration
                    </Typography>
                    <ButtonContained 
                        text="Save Changes" 
                        onClick={_updateSmsGateway}
                        disabled={isLoading}
                    />
                </Box>
            </Grid>

            <Grid item xs={12} md={6}>
                <TextLabel title="API Key" required />
                <CommonTextField
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleChange}
                    placeholder="Enter API Key"
                    error={!!error.apiKey}
                    helperText={error.apiKey}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextLabel title="Sender ID" required />
                <CommonTextField
                    name="senderId"
                    value={formData.senderId}
                    onChange={handleChange}
                    placeholder="Enter Sender ID"
                    error={!!error.senderId}
                    helperText={error.senderId}
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextLabel title="Template ID" />
                <CommonTextField
                    name="templateId"
                    value={formData.templateId}
                    onChange={handleChange}
                    placeholder="Enter Template ID"
                />
            </Grid>

            <Grid item xs={12} md={6}>
                <TextLabel title="Base URL" required />
                <CommonTextField
                    name="baseUrl"
                    value={formData.baseUrl}
                    onChange={handleChange}
                    placeholder="Enter Base URL (e.g., https://api.sms.com)"
                    error={!!error.baseUrl}
                    helperText={error.baseUrl}
                />
            </Grid>
        </Grid>
    );
};

export default SmsGateway;
